/**
 * Edit Customer Header Modal
 *
 * Modal for editing customer header information including:
 * - Customer Name
 * - Alternative Name
 * - Nickname
 * - Categories (multi-select)
 *
 * Uses React Hook Form + gray section styling matching create customer wizard
 */

import { useMemo, useEffect } from 'react';
import { Modal, Input, Row, Col, Typography, theme, Radio } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { controlHeightTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { editCustomerHeaderSchema } from '@/schemas/forms';
import type { CustomerDetailResponse } from '@/types/api/customer';
import type { CategoryListItem } from '@/types/api/category';
import { useCustomerTranslation } from '@/hooks/useCustomerTranslation';
import CategorySelectWithCreate from '@/components/shared/CategorySelectWithCreate';

const { Text } = Typography;

interface EditCustomerHeaderFormData {
  customerName: string;
  alternativeName: string;
  nickname: string;
  type: 'company' | 'individual';
  categoryIds: number[];
}

export interface EditCustomerHeaderModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: EditCustomerHeaderFormData) => Promise<void>;
  customer: CustomerDetailResponse | null;
  categories: CategoryListItem[];
  loadingCategories?: boolean;
  loading?: boolean;
  onCategoryCreated?: () => void | Promise<void>;
}

export const EditCustomerHeaderModal: React.FC<EditCustomerHeaderModalProps> = ({
  open,
  onCancel,
  onSave,
  customer,
  categories,
  loadingCategories = false,
  loading = false,
  onCategoryCreated,
}) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useCustomerTranslation();
  const { handleError, handleSuccess } = useErrorHandler();

  // Compute form values from customer data using useMemo (prevents unnecessary recalculations)
  // NOTE: Don't filter categoryIds based on categories list here - it would overwrite
  // newly created/selected categories when the list refreshes
  const formValues = useMemo(() => {
    if (!customer) {
      return undefined;
    }

    return {
      customerName: customer.name || '',
      alternativeName: customer.alternativeName || '',
      nickname: customer.nickname || '',
      type: (customer.individual ? 'individual' : 'company') as 'company' | 'individual',
      categoryIds: customer.categories?.map((c) => c.categoryId) || [],
    };
  }, [customer]); // Only depend on customer, not categories

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditCustomerHeaderFormData>({
    resolver: zodResolver(editCustomerHeaderSchema),
    defaultValues: {
      customerName: '',
      alternativeName: '',
      nickname: '',
      type: 'company',
      categoryIds: [],
    },
    // Use `values` prop for reactive updates from external data (recommended by RHF docs)
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Reset form when modal opens to ensure fresh data from API
  useEffect(() => {
    if (open && formValues) {
      reset(formValues);
    }
  }, [open, formValues, reset]);

  const handleFormSubmit = async (data: EditCustomerHeaderFormData) => {
    try {
      await onSave(data);
      handleSuccess(t('customer.messages.customerInfoUpdatedSuccess'));
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // Use standardized form styles (no custom overrides)
  const sectionTitleStyle = formStyles.sectionTitleStyle;
  const inputStyle = formStyles.inputStyle;

  return (
    <Modal
        title={t('customer.modals.editCustomerInformation')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(handleFormSubmit)}
        okText={t('customer.actions.save')}
        cancelText={t('customer.actions.cancel')}
        confirmLoading={isSubmitting || loading}
        width={800}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
      >
        <form>
          {/* Customer Information Section */}
          <div style={formStyles.fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.customerInformation')}
            </Text>

            {/* Row 1: Type (身分) Radio + Customer Name */}
            <Row gutter={[16, 16]} align="middle">
              <Col flex="auto">
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.customerName')}
                </Text>
                <Controller
                  name="customerName"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.customerName')}
                        style={{
                          ...inputStyle,
                          borderColor: errors.customerName && touchedFields.customerName ? token.colorError : undefined,
                        }}
                      />
                      {errors.customerName && touchedFields.customerName && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.customerName?.message ?? '')}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col flex="none">
                <Text style={{ ...formStyles.labelStyle, visibility: 'hidden' }}>
                  {t('customer.fields.type')}
                </Text>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Radio.Group {...field} style={{ display: 'flex', height: '44px', alignItems: 'center' }}>
                      <Radio value="company">{t('customer.customerTypes.company')}</Radio>
                      <Radio value="individual">{t('customer.customerTypes.individual')}</Radio>
                    </Radio.Group>
                  )}
                />
              </Col>
            </Row>

            {/* Row 2: Alternative Name + Nickname (not required) */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>{t('customer.fields.alternativeName')}</Text>
                <Controller
                  name="alternativeName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('customer.placeholders.alternativeName')}
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>{t('customer.fields.nickname')}</Text>
                <Controller
                  name="nickname"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('customer.placeholders.nickname')}
                      style={formStyles.inputStyle}
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Row 3: Category */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.category')}
                </Text>
                <Controller
                  name="categoryIds"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CategorySelectWithCreate
                      value={field.value}
                      onChange={field.onChange}
                      categories={categories}
                      loading={loadingCategories}
                      onCategoryCreated={onCategoryCreated}
                      error={
                        fieldState.error?.message
                          ? t(fieldState.error.message)
                          : undefined
                      }
                      placeholder={t('customer.placeholders.categories')}
                    />
                  )}
                />
              </Col>
            </Row>
          </div>
        </form>
      </Modal>
  );
};

export default EditCustomerHeaderModal;
